/**
 * One person's profile, as a slide-out beside the list.
 *
 * A panel rather than a page because the question it answers is almost always
 * asked *of a list* — "who is this row" — and navigating away to answer it
 * loses the filters and the scroll position that made the question worth
 * asking.
 *
 * Keyed by user id, not employee id. Every screen that lists people holds a
 * user id and none of them carries an employee id, so the lookup happens
 * server-side rather than by fetching the whole employee list and searching it
 * here.
 *
 * What it deliberately does not show: PAN, Aadhaar, UAN, ESIC and the bank
 * account number. The API has never returned those — absent, not masked — and
 * surfacing even a masked version would widen PII exposure rather than
 * preserve it, which is a decision needing sign-off rather than a side effect
 * of building a panel.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Paperclip, Pencil, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { isSupervisor, useAuth } from '../store/auth';
import DocumentsPanel from './DocumentsPanel';
import type {
  EmployeeDetail,
  Office,
  Page,
  ShiftPolicy,
  StoredDocument,
} from '../types';

const STATUS_TONE: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  on_notice: 'bg-amber-100 text-amber-700',
  offboarded: 'bg-gray-100 text-gray-600',
};

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex gap-3 py-1.5">
      <dt className="w-36 shrink-0 text-xs text-gray-500">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-sm text-gray-900">
        {value || <span className="text-gray-300">—</span>}
      </dd>
    </div>
  );
}

/**
 * The two fields that decide how somebody is scheduled and geofenced.
 *
 * Options are filtered to the person's own company. The server refuses one
 * from another tenant — a policy or office must belong to the employee's
 * company — but a select that can produce a 422 should not have offered the
 * option in the first place.
 */
function AssignForm({
  person,
  policies,
  offices,
  loading,
  saving,
  onCancel,
  onSave,
}: {
  person: EmployeeDetail;
  policies: ShiftPolicy[];
  offices: Office[];
  loading: boolean;
  saving: boolean;
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [policyId, setPolicyId] = useState(person.shiftPolicyId ?? '');
  const [officeId, setOfficeId] = useState(person.officeId ?? '');

  if (loading) {
    return (
      <div className="flex justify-center py-4 text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <form
      className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          shiftPolicyId: policyId || null,
          officeId: officeId || null,
        });
      }}
    >
      <label className="block text-xs text-gray-600">
        Shift Policy
        <select
          className="input mt-1"
          value={policyId}
          onChange={(e) => setPolicyId(e.target.value)}
        >
          <option value="">{policies.length ? 'Not assigned' : 'None available'}</option>
          {policies.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </label>

      <label className="block text-xs text-gray-600">
        Workplace
        <select
          className="input mt-1"
          value={officeId}
          onChange={(e) => setOfficeId(e.target.value)}
        >
          <option value="">{offices.length ? 'Not assigned' : 'None available'}</option>
          {offices.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </label>

      {/* An honest dead end rather than an empty box: the fix is real, it is
          just somewhere else. */}
      {(!policies.length || !offices.length) && (
        <p className="text-[11px] text-amber-700">
          This company has no{' '}
          {!policies.length && !offices.length
            ? 'shift policy or workplace'
            : !policies.length
              ? 'shift policy'
              : 'workplace'}{' '}
          yet. Create one under Workplaces, then come back.
        </p>
      )}

      <div className="flex gap-2">
        <button type="submit" className="btn-primary px-2.5 py-1 text-xs" disabled={saving}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Save
        </button>
        <button type="button" className="btn-ghost px-2.5 py-1 text-xs" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function EmployeeDetailPanel({
  userId,
  name,
  onClose,
}: {
  userId: string;
  /** Shown in the header while the profile loads, so it is never anonymous. */
  name?: string | null;
  onClose: () => void;
}) {
  const [showFiles, setShowFiles] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const queryClient = useQueryClient();
  // PATCH /admin/employees is Approver-gated, so a recruiter is not shown a
  // control that could only ever 403.
  const canAssign = isSupervisor(useAuth((s) => s.user));

  const employee = useQuery<EmployeeDetail>({
    queryKey: ['employee-detail', userId],
    queryFn: async () =>
      (await client.get(`/api/v1/admin/employees/by-user/${userId}`)).data,
    retry: false,
  });

  const person = employee.data;

  /**
   * Only fetched once somebody opens the editor.
   *
   * The panel is opened constantly just to read a profile, and two extra
   * requests on every open to populate a control nobody touched would be
   * paid far more often than used.
   */
  const { data: policies } = useQuery<Page<ShiftPolicy>>({
    queryKey: ['shift-policies', 'assignable'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/shift-policies', { params: { limit: 200 } })).data,
    enabled: assigning,
  });

  const { data: offices } = useQuery<Page<Office>>({
    queryKey: ['offices', 'assignable'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/offices', { params: { limit: 200 } })).data,
    enabled: assigning,
  });

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.patch(`/api/v1/admin/employees/${person?.id}`, body)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employee-detail', userId] });
      setAssigning(false);
      toast.success('Saved');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not save that')),
  });

  return (
    <div className="fixed inset-0 z-30 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        className="h-full w-full max-w-md overflow-y-auto bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              {person?.fullName ?? name ?? 'Profile'}
            </h3>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {person?.designation ?? person?.email ?? ''}
            </p>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {person && <ProfilePhoto employeeId={person.id} />}

        {employee.isLoading ? (
          <div className="flex justify-center py-12 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : employee.isError || !person ? (
          /* A user with no HR record is the ordinary case here, not a fault —
             a login can exist without an employee row behind it. */
          <p className="p-4 text-sm text-gray-500">
            No employee profile is on file for this person.
          </p>
        ) : (
          <div className="space-y-4 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`badge ${STATUS_TONE[person.status] ?? 'bg-gray-100 text-gray-600'}`}>
                {person.status.replace(/_/g, ' ')}
              </span>
              <span className="font-mono text-xs text-gray-500">
                {person.employeeCode}
              </span>
            </div>

            <dl className="divide-y divide-gray-100">
              <Row label="Designation" value={person.designation} />
              <Row label="Department" value={person.department} />
              <Row label="Reports to" value={person.reportsToName} />
              <Row label="Joined" value={person.dateOfJoining} />
              {person.dateOfExit && <Row label="Left" value={person.dateOfExit} />}
              <Row label="Shift" value={person.shiftPolicyName} />
              <Row label="Workplace" value={person.officeName} />
            </dl>

            {/* Deliberately narrow: these two fields and nothing else.
                Everything else on this record is admin-controlled through the
                onboarding flow, and an editor for all of it is its own piece
                of work. These two are here because an employee with neither is
                scheduled against a hardcoded 08:30-17:30 fallback with
                geofencing forced on and no office to check it against — and
                until now the only way to set them was during onboarding or in
                the database. */}
            {canAssign && !assigning && (
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => setAssigning(true)}
              >
                <Pencil className="h-3.5 w-3.5" />
                {person.shiftPolicyName ? 'Change shift or workplace' : 'Assign shift and workplace'}
              </button>
            )}

            {canAssign && assigning && (
              <AssignForm
                person={person}
                policies={(policies?.items ?? []).filter(
                  (p) => p.companyId === person.companyId,
                )}
                offices={(offices?.items ?? []).filter(
                  (o) => o.companyId === person.companyId,
                )}
                loading={!policies || !offices}
                saving={save.isPending}
                onCancel={() => setAssigning(false)}
                onSave={(body) => save.mutate(body)}
              />
            )}

            <div>
              <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Contact
              </h4>
              <dl className="mt-1 divide-y divide-gray-100">
                <Row label="Work email" value={person.email} />
                <Row label="Personal email" value={person.personalEmail} />
                <Row label="Emergency" value={person.emergencyContact} />
              </dl>
            </div>

            {(person.bankName || person.bankIfsc) && (
              <div>
                <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Bank
                </h4>
                <dl className="mt-1 divide-y divide-gray-100">
                  <Row label="Bank" value={person.bankName} />
                  <Row label="IFSC" value={person.bankIfsc} />
                </dl>
                {/* Said plainly, so nobody goes looking for a field that was
                    never sent and concludes the record is incomplete. */}
                <p className="mt-1.5 text-[11px] text-gray-400">
                  Account number, PAN, Aadhaar, UAN and ESIC are never returned
                  by the API and are not shown here.
                </p>
              </div>
            )}

            {person.anonymisedAt && (
              <p className="rounded bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
                Personal data for this record was erased on
                {' '}{person.anonymisedAt.slice(0, 10)}.
              </p>
            )}

            {/* The same component the candidate files use, given an employee
                id instead. Its API guard is already approver-only, so a
                recruiter cannot read these even by calling it directly. */}
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setShowFiles(true)}
            >
              <Paperclip className="h-3.5 w-3.5" />
              Files
            </button>
          </div>
        )}
      </aside>

      {showFiles && person && (
        <DocumentsPanel
          title={person.fullName ?? person.employeeCode}
          employeeId={person.id}
          onClose={() => setShowFiles(false)}
        />
      )}
    </div>
  );
}

/**
 * The employee's passport photo, if one has been uploaded.
 *
 * Read through the ordinary documents endpoints rather than a new one, so the
 * access rules and the audit trail are the existing ones: list is scoped to a
 * single employee and refuses an unfiltered call, and the download writes the
 * same audit entry it writes for any other file. Nothing here is a second
 * path to somebody's documents.
 *
 * Absent is the normal case and renders nothing at all. A placeholder avatar
 * would be a silhouette of a person who has not supplied a photograph, which
 * is decoration standing where a fact should be — and on a panel that already
 * says "no employee profile is on file" when there is none, an invented face
 * reads as data.
 *
 * The object URL is revoked when the panel closes. Without that, opening
 * twenty employees in a row leaks twenty images for the life of the tab.
 */
function ProfilePhoto({ employeeId }: { employeeId: string }) {
  const [url, setUrl] = useState<string | null>(null);

  const { data } = useQuery<Page<StoredDocument>>({
    queryKey: ['documents', 'employee', employeeId],
    queryFn: async () =>
      (
        await client.get('/api/v1/documents', {
          params: { employeeId, limit: 50 },
        })
      ).data,
  });

  // The most recent, when there is more than one. Nothing stops a second
  // photo being uploaded, and the newest is the one somebody meant.
  const photo = (data?.items ?? [])
    .filter((d) => d.docType === 'profile_photo')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

  useEffect(() => {
    if (!photo) return;
    let revoked = false;
    let objectUrl: string | null = null;

    void client
      .get(`/api/v1/documents/${photo.id}/download`, { responseType: 'blob' })
      .then((response) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(response.data as Blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        // A photo that will not load is not worth an error toast on a panel
        // somebody opened to read a designation.
      });

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photo?.id]);

  if (!photo || !url) return null;

  return (
    <div className="flex justify-center border-b border-gray-200 px-4 py-4">
      <img
        src={url}
        alt=""
        className="h-28 w-28 rounded-lg object-cover ring-1 ring-gray-200"
      />
    </div>
  );
}
